import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Smartphone, RefreshCw, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Password-free sign-in for a computer (docs/OL_ACCESS_LINKS.md §3a + §11):
 *  - Phone: a code + QR; a family phone approves it in the club app ("Approve a computer").
 *  - Email: a 6-digit code goes to the email ON FILE (never says whether the email exists).
 * Either way this browser is signed in and remembered; then onSignedIn(next) runs.
 */
export default function SignInOptions({ orgId = null, accent = '#d97706', onSignedIn }) {
  const [mode, setMode] = useState('phone');
  const [p, setP] = useState(null);
  const [state, setState] = useState('waiting');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const timer = useRef(null);

  const start = async () => {
    setState('waiting');
    try { const { data } = await axios.post(`${API_URL}/api/access/pair/start`, {}, { withCredentials: true }); setP(data); }
    catch { setState('error'); }
  };
  useEffect(() => { if (mode === 'phone') start(); return () => clearTimeout(timer.current); }, [mode]);
  useEffect(() => {
    if (!p || mode !== 'phone') return undefined;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/access/pair/${p.pair_id}?poll=${encodeURIComponent(p.poll)}`, { withCredentials: true });
        if (data.status === 'approved') { setState('approved'); onSignedIn(data.next || '/dashboard'); return; }
        if (data.status === 'expired' || data.status === 'done') { setState('expired'); return; }
      } catch { /* keep waiting */ }
      timer.current = setTimeout(tick, 2500);
    };
    timer.current = setTimeout(tick, 2500);
    return () => clearTimeout(timer.current);
  }, [p, mode, onSignedIn]);

  const sendCode = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try { await axios.post(`${API_URL}/api/access/email-code`, { email, org_id: orgId }, { withCredentials: true }); setSent(true); }
    catch (x) { setErr(x?.response?.data?.detail?.message || 'Could not send — try again'); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API_URL}/api/access/email-code/verify`, { email, code, org_id: orgId }, { withCredentials: true });
      onSignedIn(data.next || '/dashboard');
    } catch (x) { setErr(x?.response?.data?.detail?.message || 'That code is not right'); setBusy(false); }
  };

  const tab = (m, Icon, label) => (
    <button type="button" onClick={() => { setMode(m); setErr(''); }} data-testid={`signin-tab-${m}`}
      className={`flex-1 h-9 rounded-md text-sm inline-flex items-center justify-center gap-1 ${mode === m ? 'text-white' : 'border dark:border-slate-600'}`}
      style={mode === m ? { background: accent } : undefined}><Icon className="h-4 w-4" />{label}</button>
  );
  const inp = 'w-full h-10 rounded-lg border px-3 text-sm bg-white dark:bg-slate-900 dark:border-slate-600';
  return (
    <div className="space-y-4" data-testid="signin-options">
      <div className="flex gap-2">{tab('phone', Smartphone, 'With my phone')}{tab('email', Mail, 'Email me a code')}</div>
      {mode === 'phone' && (<>
        {state === 'error' && <p className="text-sm text-red-600">Could not start — try again.</p>}
        {state === 'expired' && <p className="text-sm text-slate-500">That code expired.</p>}
        {state === 'approved' && <p className="text-sm text-emerald-600">Approved — signing in…</p>}
        {p && state === 'waiting' && (<>
          <p className="text-sm text-slate-500">In your club app tap <b>Approve a computer</b> and enter this code, or scan the QR:</p>
          <p className="text-3xl font-bold tracking-widest" data-testid="pair-code">{p.code}</p>
          <img src={`${API_URL}/api/access/pair/${p.pair_id}/qr.png?c=${encodeURIComponent(p.code)}`} alt="QR code to approve this computer" className="w-40 h-40 mx-auto" />
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Waiting for approval · this computer stays signed in</p>
        </>)}
        {(state === 'expired' || state === 'error') && (
          <button className="inline-flex items-center gap-1 px-4 h-10 rounded-lg border" onClick={start}><RefreshCw className="h-4 w-4" />New code</button>
        )}
      </>)}
      {mode === 'email' && (!sent ? (
        <form onSubmit={sendCode} className="space-y-2 text-left">
          <label className="text-sm font-medium" htmlFor="signin-email">The email the club has for you</label>
          <input id="signin-email" type="email" required className={inp} value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" data-testid="signin-email" />
          <button disabled={busy} className="w-full h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2" style={{ background: accent }} data-testid="signin-email-send">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Email me a code</button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-2 text-left">
          <p className="text-sm text-slate-500">If <b>{email}</b> is on file, a 6-digit code is on its way (10 minutes).</p>
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required className={`${inp} text-center text-2xl tracking-[0.4em]`}
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} data-testid="signin-code" aria-label="6-digit code" />
          <button disabled={busy || code.length !== 6} className="w-full h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: accent }} data-testid="signin-code-verify">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Sign in</button>
          <button type="button" className="text-xs underline text-slate-500" onClick={() => { setSent(false); setCode(''); }}>Use a different email</button>
        </form>
      ))}
      {err && <p className="text-sm text-red-600" role="alert">{err}</p>}
      <p className="text-xs text-slate-500">No phone app? Ask the club to send you your personal link.</p>
    </div>
  );
}
